import { useEffect, useRef, useState } from 'react'
import { Loader2, RotateCcw } from 'lucide-react'
import toast from 'react-hot-toast'
import {
  useUpsertCitAdjustment,
  type CitAdjustmentRow as CitAdjustmentRowData,
} from '../../../hooks/useCitAdjustments'
import { useCurrency } from '../../../hooks/useCurrency'

interface Props {
  row: CitAdjustmentRowData
  disabled?: boolean       // true while frozen / while parent is posting
}

export function CitAdjustmentRow({ row, disabled }: Props) {
  const { format } = useCurrency()
  const upsert = useUpsertCitAdjustment()

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<string>('')
  const inputRef = useRef<HTMLInputElement | null>(null)

  const isOverridden = row.manualValue !== null
  const frozen = !!row.accrualId || disabled

  useEffect(() => {
    if (editing) inputRef.current?.focus()
  }, [editing])

  function startEdit() {
    setDraft(
      isOverridden
        ? String(row.manualValue ?? 0)
        : row.computedValue === 0 ? '' : String(row.computedValue),
    )
    setEditing(true)
  }

  function cancelEdit() {
    setEditing(false)
    setDraft('')
  }

  async function save() {
    const trimmed = draft.trim()
    const parsed = trimmed === '' ? null : Number(trimmed)
    if (parsed !== null && !Number.isFinite(parsed)) {
      toast.error('Enter a valid number')
      return
    }
    if (parsed !== null && parsed < 0) {
      toast.error('Adjustment values cannot be negative')
      return
    }

    // If the user typed the same value as computed, drop the override.
    const next =
      parsed === null ? null
      : Math.abs(parsed - row.computedValue) < 0.005 ? null
      : parsed

    try {
      await upsert.mutateAsync({ id: row.id, manualValue: next })
      setEditing(false)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save adjustment'
      toast.error(msg)
    }
  }

  async function reset() {
    try {
      await upsert.mutateAsync({ id: row.id, manualValue: null })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to reset adjustment'
      toast.error(msg)
    }
  }

  return (
    <div className="flex items-start justify-between gap-3 py-1.5">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm text-slate-700">{row.label}</p>
          {isOverridden && !editing && (
            <span
              title={`System computed: ${format(row.computedValue)}`}
              className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-700"
            >
              Overridden
            </span>
          )}
        </div>
        {isOverridden && !editing && !frozen && (
          <button
            type="button"
            onClick={reset}
            disabled={upsert.isPending}
            className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-brand-600 disabled:opacity-50"
          >
            <RotateCcw className="h-3 w-3" />
            Reset to computed ({format(row.computedValue)})
          </button>
        )}
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {editing ? (
          <>
            <input
              ref={inputRef}
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') void save()
                if (e.key === 'Escape') cancelEdit()
              }}
              className="w-32 rounded-lg border border-slate-300 px-2 py-1 text-right text-sm tabular-nums focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
            <button
              type="button"
              onClick={save}
              disabled={upsert.isPending}
              className="inline-flex items-center gap-1 rounded-lg bg-brand-500 px-2.5 py-1 text-xs font-medium text-white hover:bg-brand-600 disabled:opacity-50"
            >
              {upsert.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
            </button>
            <button
              type="button"
              onClick={cancelEdit}
              disabled={upsert.isPending}
              className="text-xs text-slate-500 hover:text-slate-700 disabled:opacity-50"
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <p className="tabular-nums whitespace-nowrap text-sm text-slate-800">
              {format(row.effectiveValue)}
            </p>
            {!frozen && (
              <button
                type="button"
                onClick={startEdit}
                className="rounded-lg border border-slate-200 px-2 py-0.5 text-xs font-medium text-slate-600 hover:border-brand-300 hover:text-brand-600"
              >
                Edit
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}
